import { CurrencyCode } from '@rematter/frontend-lib-api-client';
import { atom } from 'jotai';

import {
  unappliedAdvancesForAccountsQueryAtom,
  UnappliedAdvanceForBooking,
} from '../_graphql/queryUnappliedAdvancesForAccounts';

import { advanceApplyAtom } from '~/modules/finance/_graphql/mutationAdvanceApply';

// Types
export type CreatedInvoiceForAdvances = {
  accountId: string;
  accountName: string;
  invoiceId: string;
  documentNumber: string;
  amount: number;
  currencyCode: CurrencyCode;
};

export type AdvanceApplication = {
  advanceId: string;
  invoiceId: string;
  amount: number;
  currencyCode: CurrencyCode;
};

export type ApplyAdvancesResult = {
  success: AdvanceApplication[];
  failed: Array<{
    advanceId: string;
    invoiceId: string;
    error: string;
  }>;
};

// Plain atoms (Form State)
export const $isApplyAdvancesDialogOpen = atom<boolean>(false);
export const $createdInvoicesForAdvances = atom<CreatedInvoiceForAdvances[]>(
  [],
);
export const $selectedAdvanceApplications = atom<
  Map<string, AdvanceApplication>
>(new Map());
export const $isApplyingAdvances = atom<boolean>(false);

// Action atoms (Dialog Control)
export const $openApplyAdvancesDialog = atom(
  null,
  (get, set, invoices: CreatedInvoiceForAdvances[]) => {
    set($createdInvoicesForAdvances, invoices);
    set($selectedAdvanceApplications, new Map());
    set($isApplyAdvancesDialogOpen, true);
  },
);

export const $closeApplyAdvancesDialog = atom(null, (get, set) => {
  set($isApplyAdvancesDialogOpen, false);
  set($createdInvoicesForAdvances, []);
  set($selectedAdvanceApplications, new Map());
  set($isApplyingAdvances, false);
});

// Computed atoms (Derived Data)
export const $uniqueAccountIds = atom((get) => {
  const invoices = get($createdInvoicesForAdvances);
  return [...new Set(invoices.map((inv) => inv.accountId))];
});

// Query atom for advances (fetches all advances for accounts, filtered by currency in component)
export const $advancesForAccounts = unappliedAdvancesForAccountsQueryAtom(
  (get) => {
    const accountIds = get($uniqueAccountIds);

    return {
      variables: {
        accountIds,
      },
      skip: accountIds.length === 0,
    };
  },
);

export const $advancesLoading = atom((get) => {
  const data = get($advancesForAccounts);
  return data?.isLoading ?? false;
});

export const $advancesData = atom((get) => {
  const data = get($advancesForAccounts);
  return data?.data ?? [];
});

// Group all advances by account (currency filtering happens in component)
export const $advancesByAccount = atom((get) => {
  const advances = get($advancesData);
  const grouped = new Map<string, UnappliedAdvanceForBooking[]>();

  for (const advance of advances) {
    const accountId = advance.accountId;
    if (!grouped.has(accountId)) {
      grouped.set(accountId, []);
    }
    grouped.get(accountId)?.push(advance);
  }

  return grouped;
});

// Group invoices by account
export const $invoicesByAccount = atom((get) => {
  const invoices = get($createdInvoicesForAdvances);
  const grouped = new Map<string, CreatedInvoiceForAdvances[]>();

  for (const invoice of invoices) {
    const accountId = invoice.accountId;
    if (!grouped.has(accountId)) {
      grouped.set(accountId, []);
    }
    grouped.get(accountId)?.push(invoice);
  }

  return grouped;
});

// Check if there are any advances available
export const $hasAdvancesAvailable = atom((get) => {
  const advances = get($advancesData);
  return advances.length > 0;
});

// Action atoms (Selection Management)
export const $toggleAdvanceSelection = atom(
  null,
  (
    get,
    set,
    params: {
      advanceId: string;
      invoiceId: string;
      amount: number;
      currencyCode: CurrencyCode;
    },
  ) => {
    const current = get($selectedAdvanceApplications);
    const key = `${params.advanceId}-${params.invoiceId}`;
    const next = new Map(current);

    if (next.has(key)) {
      next.delete(key);
    } else {
      next.set(key, {
        advanceId: params.advanceId,
        invoiceId: params.invoiceId,
        amount: params.amount,
        currencyCode: params.currencyCode,
      });
    }

    set($selectedAdvanceApplications, next);
  },
);

export const $updateAdvanceAmount = atom(
  null,
  (
    get,
    set,
    params: {
      advanceId: string;
      invoiceId: string;
      amount: number;
    },
  ) => {
    const current = get($selectedAdvanceApplications);
    const key = `${params.advanceId}-${params.invoiceId}`;

    if (current.has(key)) {
      const next = new Map(current);
      const existing = next.get(key);
      if (!existing) return;
      next.set(key, { ...existing, amount: params.amount });
      set($selectedAdvanceApplications, next);
    }
  },
);

export const $selectedCount = atom((get) => {
  const selections = get($selectedAdvanceApplications);
  return selections.size;
});

// Mutation atom
const $applyAdvance = advanceApplyAtom();

// Apply all selected advances
export const $applySelectedAdvances = atom(
  null,
  async (get, set): Promise<ApplyAdvancesResult> => {
    const selections = get($selectedAdvanceApplications);

    if (selections.size === 0) {
      return { success: [], failed: [] };
    }

    set($isApplyingAdvances, true);

    const success: AdvanceApplication[] = [];
    const failed: ApplyAdvancesResult['failed'] = [];

    try {
      // Apply advances sequentially to avoid race conditions
      for (const [, application] of selections) {
        try {
          await set($applyAdvance, {
            paymentId: application.advanceId,
            documentId: application.invoiceId,
            documentType: 'Invoice',
            appliedAmount: {
              amount: application.amount,
              currencyCode: application.currencyCode,
            },
          });

          success.push(application);
        } catch (error) {
          failed.push({
            advanceId: application.advanceId,
            invoiceId: application.invoiceId,
            error: error instanceof Error ? error.message : 'Unknown error',
          });
        }
      }

      return { success, failed };
    } finally {
      set($isApplyingAdvances, false);
    }
  },
);
